'use client';

import { useCallback, useMemo } from 'react';

const useSearch = <T, V>(
  values: T[],
  initialValue: T | null | undefined,
  value: T | null | undefined,
  toLabel: (v: T) => string,
  toValue: (v: T) => V
) => {
  const makeOption = useCallback(
    (value: T) => ({
      label: toLabel(value),
      value: toValue(value),
    }),
    [toLabel, toValue]
  );

  const options = useMemo(() => values.map(makeOption), [makeOption, values]);

  const selectedOption = useMemo(
    () => (value ? makeOption(value) : undefined),
    [makeOption, value]
  );

  const initialOption = useMemo(
    () => (initialValue ? makeOption(initialValue) : undefined),
    [makeOption, initialValue]
  );

  return useMemo(
    () => ({ options, selectedOption, initialOption }),
    [options, selectedOption, initialOption]
  );
};

export default useSearch;
